import React, { useEffect, useState } from 'react';
import BrokerLayout from '@/components/BrokerLayout';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Users, Phone, MessageCircle, Mail, Calendar, MapPin, Building2 } from 'lucide-react';

const LEAD_STATUSES = [
  'new', 'contacted', 'qualified', 'follow_up', 'site_visit_scheduled',
  'site_visit_completed', 'negotiation', 'booked', 'closed_won', 'closed_lost', 'not_interested'
];

const statusColor = (s) => {
  if (['booked', 'closed_won'].includes(s)) return 'bg-emerald-50 text-emerald-700 border-emerald-200';
  if (['closed_lost', 'not_interested'].includes(s)) return 'bg-red-50 text-red-700 border-red-200';
  if (s === 'new') return 'bg-blue-50 text-blue-700 border-blue-200';
  return 'bg-amber-50 text-amber-700 border-amber-200';
};

const BrokerLeads = () => {
  const [leads, setLeads] = useState([]);
  const [profile, setProfile] = useState(null);
  const [reminders, setReminders] = useState(null);
  const [selected, setSelected] = useState(null);
  const [filter, setFilter] = useState('all');
  const [update, setUpdate] = useState({ status: '', priority: '', note: '', follow_up_date: '', site_visit_date: '' });

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    try {
      const [l, p, r] = await Promise.all([
        api.get('/broker/leads'),
        api.get('/broker/profile').catch(() => ({ data: null })),
        api.get('/broker/reminders').catch(() => ({ data: null }))
      ]);
      setLeads(l.data);
      setProfile(p.data);
      setReminders(r.data);
    } catch (e) { console.error(e); }
  };

  const openLead = (lead) => {
    setSelected(lead);
    setUpdate({ status: '', priority: lead.priority || '', note: '', follow_up_date: lead.follow_up_date || '', site_visit_date: lead.site_visit_date || '' });
  };

  const saveUpdate = async () => {
    if (!selected) return;
    try {
      const payload = {};
      if (update.status) payload.status = update.status;
      if (update.priority) payload.priority = update.priority;
      if (update.note) payload.note = update.note;
      if (update.follow_up_date) payload.follow_up_date = update.follow_up_date;
      if (update.site_visit_date) payload.site_visit_date = update.site_visit_date;
      await api.put(`/broker/leads/${selected.id}`, payload);
      toast.success('Lead updated');
      setSelected(null);
      load();
    } catch (e) { toast.error('Update failed'); }
  };

  const filteredLeads = filter === 'all' ? leads : leads.filter(l => l.status === filter);

  return (
    <BrokerLayout profile={profile} remindersCount={reminders?.urgent_count || 0}>
      <div className="mb-6">
        <h1 className="font-display font-extrabold text-2xl sm:text-3xl text-[#0F2C59]" data-testid="broker-leads-title">Leads CRM</h1>
        <p className="text-sm text-slate-500 mt-1">Manage buyer enquiries and track conversions</p>
      </div>

      {/* Filter tabs */}
      <div className="mb-4 flex flex-wrap gap-2">
        <button onClick={() => setFilter('all')} className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${filter === 'all' ? 'bg-[#0F2C59] text-white border-[#0F2C59]' : 'bg-white text-slate-700 border-slate-200 hover:border-slate-300'}`} data-testid="filter-all">
          All ({leads.length})
        </button>
        {LEAD_STATUSES.map(s => {
          const count = leads.filter(l => l.status === s).length;
          if (count === 0) return null;
          return (
            <button key={s} onClick={() => setFilter(s)} className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${filter === s ? 'bg-[#0F2C59] text-white border-[#0F2C59]' : 'bg-white text-slate-700 border-slate-200 hover:border-slate-300'}`} data-testid={`filter-${s}`}>
              {s.replace(/_/g, ' ')} ({count})
            </button>
          );
        })}
      </div>

      {filteredLeads.length === 0 ? (
        <div className="bg-white rounded-lg border border-dashed border-slate-300 p-12 text-center">
          <Users size={40} className="mx-auto text-slate-300 mb-3" />
          <div className="text-slate-500 font-medium">No leads {filter !== 'all' && `in ${filter}`}</div>
          <div className="text-xs text-slate-400 mt-1">Enquiries from your landing pages will appear here</div>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold">Buyer</th>
                  <th className="text-left px-4 py-3 font-semibold">Contact</th>
                  <th className="text-left px-4 py-3 font-semibold">Source</th>
                  <th className="text-left px-4 py-3 font-semibold">Status</th>
                  <th className="text-left px-4 py-3 font-semibold">Received</th>
                  <th className="text-left px-4 py-3 font-semibold">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredLeads.map(lead => (
                  <tr key={lead.id} className="hover:bg-slate-50" data-testid={`lead-row-${lead.id}`}>
                    <td className="px-4 py-3">
                      <div className="font-semibold text-slate-900">{lead.name}</div>
                      {lead.message && <div className="text-xs text-slate-500 line-clamp-1 max-w-xs mt-0.5">{lead.message}</div>}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <a href={`tel:${lead.mobile}`} className="text-[#0F2C59] hover:text-[#F97316]" title="Call" data-testid={`lead-call-${lead.id}`}><Phone size={14}/></a>
                        {lead.whatsapp && <a href={`https://wa.me/${lead.whatsapp.replace(/\D/g, '')}`} target="_blank" rel="noreferrer" className="text-emerald-600 hover:text-emerald-700" title="WhatsApp"><MessageCircle size={14}/></a>}
                        {lead.email && <a href={`mailto:${lead.email}`} className="text-slate-500 hover:text-slate-700" title="Email"><Mail size={14}/></a>}
                      </div>
                      <div className="text-xs font-mono text-slate-600 mt-1">{lead.mobile}</div>
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-600 capitalize">{lead.source?.replace(/_/g, ' ')}</td>
                    <td className="px-4 py-3">
                      <Badge className={`text-xs ${statusColor(lead.status)} border`}>{lead.status?.replace(/_/g, ' ')}</Badge>
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-500">{new Date(lead.created_at).toLocaleDateString('en-IN')}</td>
                    <td className="px-4 py-3">
                      <Button size="sm" variant="outline" onClick={() => openLead(lead)} data-testid={`lead-manage-${lead.id}`}>Manage</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Lead Detail Dialog */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle>{selected.name}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                {/* Contact Bar */}
                <div className="flex items-center gap-2 flex-wrap">
                  <a href={`tel:${selected.mobile}`} className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#0F2C59] text-white rounded-md text-sm font-semibold" data-testid="lead-call-btn"><Phone size={14}/> Call {selected.mobile}</a>
                  {selected.whatsapp && <a href={`https://wa.me/${selected.whatsapp.replace(/\D/g, '')}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-3 py-1.5 bg-emerald-600 text-white rounded-md text-sm font-semibold" data-testid="lead-whatsapp-btn"><MessageCircle size={14}/> WhatsApp</a>}
                  {selected.email && <a href={`mailto:${selected.email}`} className="inline-flex items-center gap-2 px-3 py-1.5 bg-slate-700 text-white rounded-md text-sm font-semibold"><Mail size={14}/> Email</a>}
                </div>

                {/* Details */}
                <div className="grid grid-cols-2 gap-3 text-sm">
                  {selected.email && <div><div className="text-xs uppercase text-slate-500 font-semibold">Email</div><div>{selected.email}</div></div>}
                  {selected.city && <div><div className="text-xs uppercase text-slate-500 font-semibold">City</div><div>{selected.city}</div></div>}
                  {selected.budget_min && <div><div className="text-xs uppercase text-slate-500 font-semibold">Budget</div><div>₹{selected.budget_min?.toLocaleString('en-IN')} - ₹{selected.budget_max?.toLocaleString('en-IN')}</div></div>}
                  {selected.preferred_location && <div><div className="text-xs uppercase text-slate-500 font-semibold">Preferred Location</div><div>{selected.preferred_location}</div></div>}
                </div>

                {selected.message && (
                  <div>
                    <div className="text-xs uppercase text-slate-500 font-semibold mb-1">Message</div>
                    <div className="p-3 bg-slate-50 rounded-md text-sm">{selected.message}</div>
                  </div>
                )}

                {/* Update Panel */}
                <div className="pt-4 border-t space-y-3">
                  <h4 className="font-bold text-sm text-[#0F2C59]">Update Lead</h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Status</Label>
                      <Select value={update.status} onValueChange={(v) => setUpdate({...update, status: v})}>
                        <SelectTrigger data-testid="update-status-select"><SelectValue placeholder="Change status"/></SelectTrigger>
                        <SelectContent>
                          {LEAD_STATUSES.map(s => <SelectItem key={s} value={s}>{s.replace(/_/g, ' ')}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Priority</Label>
                      <Select value={update.priority} onValueChange={(v) => setUpdate({...update, priority: v})}>
                        <SelectTrigger data-testid="update-priority-select"><SelectValue placeholder="Priority"/></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="low">Low</SelectItem>
                          <SelectItem value="medium">Medium</SelectItem>
                          <SelectItem value="high">High</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Follow-up Date</Label>
                      <Input type="date" value={update.follow_up_date} onChange={(e) => setUpdate({...update, follow_up_date: e.target.value})} data-testid="update-followup-input" />
                    </div>
                    <div>
                      <Label>Site Visit Date</Label>
                      <Input type="date" value={update.site_visit_date} onChange={(e) => setUpdate({...update, site_visit_date: e.target.value})} data-testid="update-sitevisit-input" />
                    </div>
                  </div>
                  <div>
                    <Label>Add Note</Label>
                    <Textarea value={update.note} onChange={(e) => setUpdate({...update, note: e.target.value})} rows={3} placeholder="Called buyer, scheduled visit for..." data-testid="update-note-input" />
                  </div>
                  <Button onClick={saveUpdate} className="w-full bg-[#0F2C59] hover:bg-[#0F2C59]/90" data-testid="save-lead-update-btn">Save Update</Button>
                </div>

                {/* Notes History */}
                {selected.notes?.length > 0 && (
                  <div className="pt-4 border-t">
                    <h4 className="font-bold text-sm text-[#0F2C59] mb-3">Notes History</h4>
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {selected.notes.map((n, i) => (
                        <div key={i} className="p-3 bg-slate-50 rounded-md">
                          <div className="text-xs text-slate-500 mb-1">{new Date(n.created_at).toLocaleString('en-IN')}</div>
                          <div className="text-sm">{n.text}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </BrokerLayout>
  );
};

export default BrokerLeads;
